// ConversationList.jsx — v4.0. Refonte iOS : avatars déterministes, recherche
// dans les conversations (local), squelettes de chargement, réglages étendus
// (export d'historique, suppression de compte), bottom sheets de confirmation.
// v4.1 : notes personnelles épinglées, QR code (ajout/vérif contact),
// contacts bloqués, taille de police / contraste élevé, rappel d'export.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { exportIdentity } from '../crypto.js';
import { qrPayload, parseQrPayload } from '../qr.js';
import { Avatar, Icon, QrCode, QrScanner, APP_VERSION } from '../ui.jsx';
import { buildConversationView, normalizeBlocked, previewOf } from '../safe-data.js';
import { clearCallHistory, readCallHistory } from '../call-history.js';
import { getAllMessageMetadata, auditVaultAtRest } from '../storage.js';
import { openLocalString } from '../local-vault.js';
import { normalizeConversationKeys, searchLocalMessages } from '../messaging-tools.js';

const BUBBLE_COLORS = [
  ['blue', 'Bleu'], ['purple', 'Violet'], ['green', 'Vert'], ['teal', 'Turquoise'],
  ['graphite', 'Gris foncé'], ['orange', 'Orange doux'], ['pink', 'Rose doux'],
];

function formatBytes(value) {
  const n = Math.max(0, Number(value) || 0);
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

const CALL_OUTCOME_LABELS = {
  completed: 'Terminé', missed: 'Manqué', 'no-answer': 'Sans réponse', declined: 'Refusé', busy: 'Occupé', cancelled: 'Annulé', failed: 'Échec',
};
const QUALITY_LABELS = { excellent: 'excellente', good: 'bonne', weak: 'moyenne', poor: 'faible' };
function formatCallDuration(seconds) {
  const n = Math.max(0, Number(seconds) || 0);
  if (!n) return '';
  const m = Math.floor(n / 60), s = Math.floor(n % 60);
  return m ? `${m} min ${String(s).padStart(2, '0')} s` : `${s} s`;
}

const FONT_SCALES = [
  { key: 'sm', label: 'Petit' }, { key: 'md', label: 'Normal' },
  { key: 'lg', label: 'Grand' }, { key: 'xl', label: 'Très grand' },
];

export default function ConversationList({
  identity, contacts = [], groups = [], convMeta = {}, status,
  notifEnabled, receiptsEnabled, darkMode, fontScale, highContrast,
  blocked = [], exportReminder,
  secured, storagePersisted, bubbleColor = 'blue', uiStyle = 'classic', pinnedConversations = [], archivedConversations = [], lastExportAt = 0,
  isAdmin = false, adminPreview = null, adminResult = null, adminError = '', appLockConfigured = false, vaultEnabled = false, vaultBiometricEnabled = false, vaultBiometricAvailable = false, vaultLockPolicy = 'launch', vaultAutomatic = false,
  onToggleNotifications = () => {}, onToggleReceipts = () => {}, onToggleDark = () => {},
  onSetFontScale = () => {}, onToggleHighContrast = () => {}, onSetBubbleColor = () => {}, onSetUiStyle = () => {}, onSetConversationPinned = () => {}, onSetConversationArchived = () => {}, onRepairApp = () => {},
  onAdminPreview = () => {}, onAdminExecute = () => {}, onAdminCancel = () => {}, onConfigureAppLock = async () => {}, onDisableAppLock = () => {}, onLockAppNow = () => {}, onChangeVaultCode = async () => {}, onEnableVaultBiometric = async () => {}, onDisableVaultBiometric = async () => {}, onSetVaultLockPolicy = async () => {}, onEnableVaultAutomatic = async () => {}, onDisableVaultAutomatic = async () => {}, onLockVaultNow = () => {}, onExportVaultBackup = async () => {}, onImportVaultBackup = async () => {},
  onToggleBlocked = () => {}, onDismissExportReminder = () => {}, onSecure = () => {},
  onExportHistory = () => {}, onImportHistory = () => {}, onDeleteAccount = () => {},
  onCreateGroup = async () => '', onOpen = async () => {}, onDeleteConversation = () => {}, onStartAudioCallFor = async () => {}, onStartVideoCallFor = async () => {}, audioCallAvailable = false, videoCallAvailable = false,
  onAddContactByQr = () => {}, onLogout = () => {},
}) {
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState('');
  const [exportPass, setExportPass] = useState('');
  const [histPass, setHistPass] = useState('');
  const [confirmDelConv, setConfirmDelConv] = useState(null); // { key, label }
  const [confirmDelAccount, setConfirmDelAccount] = useState(false);
  const [delAccountText, setDelAccountText] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [showMyQr, setShowMyQr] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [bannerLocal, setBannerLocal] = useState('');
  const [health, setHealth] = useState(null); // v4.2.1 : remonté avec les autres hooks (règles de React, cf. garde ci-dessous)
  const [messageResults, setMessageResults] = useState([]);
  const [searchingMessages, setSearchingMessages] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [adminPhrase, setAdminPhrase] = useState('');
  const [lockCode, setLockCode] = useState('');
  const [lockCode2, setLockCode2] = useState('');
  const [vaultOldCode, setVaultOldCode] = useState('');
  const [vaultNewCode, setVaultNewCode] = useState('');
  const [vaultNewCode2, setVaultNewCode2] = useState('');
  const [vaultBackupPass, setVaultBackupPass] = useState('');
  const [vaultStrongCode, setVaultStrongCode] = useState('');
  const [vaultStrongCode2, setVaultStrongCode2] = useState('');
  const [vaultBackupFile, setVaultBackupFile] = useState(null);
  const [openConversationMenu, setOpenConversationMenu] = useState('');

  useEffect(() => { setAdminPhrase(''); }, [adminPreview?.token]);

  function closeConversationMenu() { setOpenConversationMenu(''); }
  function toggleConversationMenu(event, key) {
    event.stopPropagation();
    setOpenConversationMenu((current) => current === key ? '' : key);
  }

  // v4.9.3 : le menu n'est plus positionné relativement à la ligne. Un portal
  // l'attache directement à document.body, au-dessus de tous les contextes de
  // stacking Safari. Le fond plein écran ferme le menu au moindre tap externe.
  useEffect(() => {
    if (!openConversationMenu) return undefined;
    const closeOnEscape = (event) => { if (event.key === 'Escape') closeConversationMenu(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [openConversationMenu]);

  useEffect(() => {
    if (!showSettings) return undefined;
    let cancelled = false;
    (async () => {
      const result = {
        appVersion: APP_VERSION,
        online: navigator.onLine,
        standalone: window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true,
        serviceWorker: 'serviceWorker' in navigator,
        serviceWorkerControlled: !!navigator.serviceWorker?.controller,
        pushSupported: 'PushManager' in window,
        notificationPermission: 'Notification' in window ? Notification.permission : 'indisponible',
        storagePersisted,
        browser: navigator.userAgent,
      };
      try {
        const raw = JSON.parse(openLocalString('audio-network', localStorage.getItem('messagerie:last-audio-network:v1') || '') || 'null');
        if (raw && ['p2p', 'turn'].includes(raw.route)) {
          result.lastAudioNetwork = {
            route: raw.route,
            protocol: ['udp', 'tcp'].includes(raw.protocol) ? raw.protocol : 'unknown',
            relayProtocol: ['udp', 'tcp'].includes(raw.relayProtocol) ? raw.relayProtocol : 'unknown',
            at: Number(raw.at || 0),
          };
        }
      } catch (_) { result.lastAudioNetwork = null; }
      try {
        const raw = JSON.parse(openLocalString('audio-quality', localStorage.getItem('messagerie:last-audio-quality:v1') || '') || 'null');
        if (raw && ['excellent', 'good', 'weak', 'poor'].includes(raw.level)) {
          result.lastAudioQuality = {
            level: raw.level,
            rttMs: Number.isFinite(Number(raw.rttMs)) ? Number(raw.rttMs) : null,
            jitterMs: Number.isFinite(Number(raw.jitterMs)) ? Number(raw.jitterMs) : null,
            packetLossPct: Number.isFinite(Number(raw.packetLossPct)) ? Number(raw.packetLossPct) : null,
            at: Number(raw.at || 0),
          };
        }
      } catch (_) { result.lastAudioQuality = null; }
      result.callHistory = readCallHistory();
      try {
        const reg = await navigator.serviceWorker?.ready;
        result.pushSubscribed = !!(await reg?.pushManager?.getSubscription());
      } catch (_) { result.pushSubscribed = false; }
      try {
        const response = await fetch('/health', { cache: 'no-store' });
        if (response.ok) result.serverHealth = await response.json();
      } catch (_) { result.serverHealth = null; }
      try {
        const response = await fetch('/api/server-stats', { cache: 'no-store' });
        if (response.ok) result.serverStats = await response.json();
      } catch (_) { result.serverStats = null; }
      try {
        const estimate = await navigator.storage?.estimate?.();
        result.storageBytes = Number(estimate?.usage || 0);
      } catch (_) { result.storageBytes = 0; }
      try { result.vaultAudit = await auditVaultAtRest(); } catch (_) { result.vaultAudit = null; }
      if (!cancelled) setHealth(result);
    })();
    return () => { cancelled = true; };
  }, [showSettings, storagePersisted, adminResult]);

  // v4.6.0 : recherche plein texte strictement LOCALE dans les messages déjà
  // déchiffrés/stockés dans IndexedDB. Aucun terme de recherche ne quitte le navigateur.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setMessageResults([]); setSearchingMessages(false); return undefined; }
    let cancelled = false;
    setSearchingMessages(true);
    const timer = setTimeout(async () => {
      try {
        const all = await getAllMessageMetadata();
        if (!cancelled) setMessageResults(searchLocalMessages(all, q, { limit: 40 }));
      } catch (_) { if (!cancelled) setMessageResults([]); }
      finally { if (!cancelled) setSearchingMessages(false); }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  // v4.3.7 : normalisation de TOUTES les données externes avant le rendu.
  // Les valeurs par défaut de paramètres ne couvrent pas `null`, ni les lignes
  // IndexedDB anciennes dont un champ texte contient un objet. Le correctif
  // v4.3.5 ne protégeait que Avatar ; la liste pouvait encore rendre cet objet
  // directement et faire tomber React. Ici, chaque libellé/aperçu/compteur est
  // garanti sûr et les clés spéciales ne lisent jamais Object.prototype.
  const {
    identityPseudo,
    items,
    convMeta: safeConvMeta,
  } = buildConversationView(identity, contacts, groups, convMeta);
  const safeBlocked = normalizeBlocked(blocked);
  const safePinned = normalizeConversationKeys(pinnedConversations);
  const safeArchived = normalizeConversationKeys(archivedConversations);
  const pinnedSet = new Set(safePinned);
  const archivedSet = new Set(safeArchived);
  const identityPublicKey = typeof identity?.publicKey === 'string' ? identity.publicKey : '';

  // Garde APRÈS tous les hooks (règles de React). Une identité locale sans
  // pseudo est inutilisable, mais on affiche une issue contrôlée au lieu d'un
  // écran blanc et on ne supprime rien automatiquement.
  if (!identity || !identityPseudo) {
    return (
      <div className="screen pad">
        <p className="error">Identité locale illisible : pseudo manquant.</p>
        <button type="button" className="ghost" onClick={() => location.reload()}>Recharger</button>
        <button type="button" className="danger" onClick={onLogout}>Effacer cette session locale</button>
      </div>
    );
  }

  // Recherche : sur les libellés ET l'aperçu déjà déchiffré localement.
  const q = search.trim().toLowerCase();
  const orderedItems = [...items].sort((a, b) => {
    const ap = a.pinned || pinnedSet.has(a.key); const bp = b.pinned || pinnedSet.has(b.key);
    if (ap !== bp) return ap ? -1 : 1;
    return (safeConvMeta[b.key]?.last?.sentAt || 0) - (safeConvMeta[a.key]?.last?.sentAt || 0);
  });
  const visibleItems = orderedItems.filter((it) => it.pinned || !archivedSet.has(it.key));
  const archivedItems = orderedItems.filter((it) => !it.pinned && archivedSet.has(it.key));
  const searchPool = q ? orderedItems : visibleItems;
  const filtered = q
    ? searchPool.filter((it) =>
        it.label.toLowerCase().includes(q) ||
        previewOf(safeConvMeta[it.key]).toLowerCase().includes(q))
    : visibleItems;

  async function startConversation(e) {
    e.preventDefault();
    setError('');
    const p = search.trim();
    if (!p) return;
    try { await onOpen(p); setSearch(''); } catch (err) { setError(err.message); }
  }
  async function handleScanResult(raw) {
    setShowScanner(false);
    const parsed = parseQrPayload(raw);
    if (!parsed) { setError('QR code non reconnu.'); return; }
    setError('');
    try {
      await onAddContactByQr(parsed.pseudo, parsed.publicKey);
      await onOpen(parsed.pseudo);
    } catch (err) { setError(err.message); }
  }
  async function createGroup(e) {
    e.preventDefault(); setError('');
    try {
      const members = groupMembers.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      const id = await onCreateGroup(groupName.trim() || 'Groupe', members);
      setShowNewGroup(false); setGroupName(''); setGroupMembers('');
      await onOpen(id);
    } catch (err) { setError(err.message); }
  }
  function doExportIdentity(e) {
    e.preventDefault();
    if (exportPass.length < 8) return setError('Passphrase : 8 caractères minimum.');
    setError('');
    const blob = new Blob([exportIdentity(identity, exportPass)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `identite-${identityPseudo}.json`; a.click();
    URL.revokeObjectURL(url); setExportPass('');
  }
  async function doExportHistory(e) {
    e.preventDefault();
    if (histPass.length < 8) return setError('Mot de passe : 8 caractères minimum.');
    setError('');
    await onExportHistory(histPass); setHistPass('');
  }
  async function doImportHistory() {
    if (!importFile || histPass.length < 8) return setError('Fichier + mot de passe (8 car. min) requis.');
    setError('');
    try { await onImportHistory(importFile, histPass); setImportFile(null); setHistPass(''); setBannerLocal('Historique importé.'); }
    catch (err) { setError(err.message); }
  }

  return (
    <div className="screen">
      <header className="bar">
        <Avatar name={identityPseudo} size="sm" />
        <div className="grow">
          <strong>{identityPseudo}</strong>
          <span className={`dot ${status === 'connected' ? 'on' : 'off'}`} />
        </div>
        <button className="ghost" onClick={() => setShowSettings(!showSettings)} title="Réglages"><Icon.gear /></button>
      </header>

      {exportReminder && (
        <div className="notice" onClick={onDismissExportReminder}>
          💾 Ça fait un moment que vous n'avez pas exporté votre historique (Réglages →
          « Exporter l'historique »). Aucune synchronisation entre appareils : sans export,
          un historique perdu ne revient pas. (cliquer pour fermer)
        </div>
      )}

      {showSettings && (
        <div className="settings">
          <label><input type="checkbox" style={{ width: 'auto' }} checked={notifEnabled} onChange={onToggleNotifications} />
            <span>Notifications push + son (jamais le contenu)</span></label>
          <label><input type="checkbox" style={{ width: 'auto' }} checked={receiptsEnabled} onChange={onToggleReceipts} />
            <span>Accusés de lecture (off = personne ne sait quand vous lisez)</span></label>
          <label><input type="checkbox" style={{ width: 'auto' }} checked={darkMode} onChange={onToggleDark} />
            <span>Mode sombre</span></label>
          <label><input type="checkbox" style={{ width: 'auto' }} checked={highContrast} onChange={onToggleHighContrast} />
            <span>Contraste élevé (accessibilité)</span></label>
          <div className="row" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span className="small muted">Taille du texte :</span>
            {FONT_SCALES.map((f) => (
              <button key={f.key} type="button" className={fontScale === f.key ? '' : 'ghost'}
                style={{ padding: '4px 10px' }} onClick={() => onSetFontScale(f.key)}>{f.label}</button>
            ))}
          </div>
          <div className="setting-block">
            <span className="small muted">Apparence de l’application :</span>
            <div className="segmented-control" role="group" aria-label="Apparence">
              <button type="button" className={uiStyle === 'classic' ? 'selected' : ''} onClick={() => onSetUiStyle('classic')}>Classique</button>
              <button type="button" className={uiStyle === 'modern' ? 'selected' : ''} onClick={() => onSetUiStyle('modern')}>Moderne</button>
            </div>
            <span className="small muted">Le mode Moderne ne change que la présentation.</span>
          </div>
          <div className="setting-block">
            <span className="small muted">Couleur des bulles envoyées (le rouge reste réservé aux messages prioritaires) :</span>
            <div className="bubble-palette">
              {BUBBLE_COLORS.map(([key, label]) => (
                <button key={key} type="button" className={`color-choice ${bubbleColor === key ? 'selected' : ''}`}
                  data-color={key} onClick={() => onSetBubbleColor(key)} aria-label={label} title={label} />
              ))}
            </div>
          </div>
          <div className="health-card">
            <strong>🩺 Santé de l’application</strong>
            {!health ? <span className="small muted">Analyse…</span> : <>
              <span className="small">App {health.appVersion} · WebSocket {status === 'connected' ? 'connecté' : 'déconnecté'}</span>
              <span className="small">PWA {health.standalone ? 'installée' : 'onglet navigateur'} · SW {health.serviceWorkerControlled ? 'actif' : health.serviceWorker ? 'présent, non contrôleur' : 'indisponible'}</span>
              <span className="small">Push {health.pushSupported ? (health.pushSubscribed ? 'abonné' : 'non abonné') : 'indisponible'} · permission {health.notificationPermission}</span>
              <span className="small">Stockage {health.storagePersisted ? 'persistant' : 'non garanti'}{health.storageBytes ? ` · ${Math.round(health.storageBytes / 1024 / 1024)} Mo utilisés` : ''}</span>
              {health.vaultAudit && <span className={`small ${health.vaultAudit.plaintextLegacyRows === 0 ? '' : 'error'}`}>🔐 Stockage local {health.vaultAudit.configured ? (health.vaultAudit.automatic ? 'chiffré · mode pratique' : 'chiffré · mode renforcé') : 'absent'} · {health.vaultAudit.encryptedRows} entrées chiffrées · plaintext legacy {health.vaultAudit.plaintextLegacyRows}</span>}
              <span className="small">Appels audio/vidéo {health.serverHealth?.audioCalls?.productionReady ? (health.serverHealth.audioCalls.relayOnly ? 'prêts · TURN forcé' : 'prêts · P2P/STUN + secours TURN') : health.serverHealth?.audioCalls?.enabled ? 'mode STUN uniquement' : 'indisponibles'}{health.serverHealth?.audioCalls?.reason ? ` · ${health.serverHealth.audioCalls.reason}` : ''}</span>
              {health.lastAudioNetwork && <span className="small muted">Dernier appel : {health.lastAudioNetwork.route === 'turn' ? 'relais TURN' : 'connexion P2P'}{health.lastAudioNetwork.protocol !== 'unknown' ? ` · ${health.lastAudioNetwork.protocol.toUpperCase()}` : ''}{health.lastAudioNetwork.route === 'turn' && health.lastAudioNetwork.relayProtocol !== 'unknown' ? `/${health.lastAudioNetwork.relayProtocol.toUpperCase()}` : ''}{health.lastAudioNetwork.at ? ` · ${new Date(health.lastAudioNetwork.at).toLocaleString('fr-FR')}` : ''}</span>}
              {health.lastAudioQuality && <span className="small muted">Qualité dernier appel : {QUALITY_LABELS[health.lastAudioQuality.level] || health.lastAudioQuality.level}{health.lastAudioQuality.rttMs != null ? ` · ${Math.round(health.lastAudioQuality.rttMs)} ms` : ''}{health.lastAudioQuality.packetLossPct != null ? ` · pertes ${health.lastAudioQuality.packetLossPct}%` : ''}</span>}
              {health.serverStats && <div className="server-stats">
                <span className="small"><strong>Nettoyage serveur depuis {health.serverStats.since}</strong></span>
                <span className="small">🗑️ {health.serverStats.deleted.messages} messages supprimés · {health.serverStats.deleted.media} médias</span>
                <span className="small">💾 {formatBytes(health.serverStats.deleted.mediaBytes)} libérés · {health.serverStats.deleted.expired} expirés</span>
                <span className="small muted">En attente : {health.serverStats.pending.messages} messages · {health.serverStats.pending.media} médias ({formatBytes(health.serverStats.pending.mediaBytes)})</span>
                {health.serverStats.calls && <span className="small muted">Appels : {health.serverStats.calls.pendingSignals} signaux E2E éphémères en attente · {health.serverStats.calls.expiredSignals} expirés</span>}
              </div>}
            </>}
            {health?.callHistory?.length > 0 && <div className="call-history-card">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <strong className="small">📞 Historique local des appels</strong>
                <button type="button" className="ghost small" onClick={() => {
                  clearCallHistory();
                  setHealth((prev) => prev ? { ...prev, callHistory: [] } : prev);
                }}>Effacer</button>
              </div>
              {health.callHistory.slice(0, 8).map((entry, index) => (
                <div className="call-history-row" key={`${entry.startedAt}-${entry.peer}-${index}`}>
                  <span>{entry.media === 'video' ? '📹' : '📞'} {entry.direction === 'incoming' ? '↙' : '↗'} <strong>{entry.peer}</strong></span>
                  <span className="small muted">{CALL_OUTCOME_LABELS[entry.outcome] || entry.outcome}{entry.durationSeconds ? ` · ${formatCallDuration(entry.durationSeconds)}` : ''}</span>
                  <span className="small muted">{new Date(entry.startedAt).toLocaleString('fr-FR')}</span>
                </div>
              ))}
              <span className="small muted">Stocké uniquement sur cet appareil. Jamais envoyé au serveur.</span>
            </div>}
            <button type="button" className="ghost" onClick={() => {
              if (window.confirm('Réparer les caches statiques et recharger ? Vos messages, votre identité et IndexedDB ne seront pas supprimés.')) onRepairApp();
            }}>🔧 Réparer l’app sans perdre les données</button>
          </div>

          <div className="setting-block vault-settings">
            <strong>🧰 Coffre local chiffré</strong>
            <span className="small muted">{vaultEnabled ? (vaultAutomatic ? 'Mode pratique : données toujours chiffrées, ouverture automatique sur cet appareil. Plus confortable, mais protection locale réduite si tout le profil navigateur est extrait.' : 'Mode renforcé : Argon2id + clé maître aléatoire + secretbox. Le coffre demande un code ou l’authentification système à l’ouverture.') : 'Coffre non ouvert.'}</span>
            {vaultEnabled && <div className="vault-mode-block">
              <strong className="small">Mode d’ouverture</strong>
              {!vaultAutomatic ? <button type="button" className="ghost" onClick={async () => {
                if (!window.confirm('Activer le mode pratique ? Le coffre s’ouvrira automatiquement sur cet appareil. Les données restent chiffrées, mais une extraction complète du profil navigateur pourrait aussi récupérer la clé d’ouverture locale.')) return;
                try { await onEnableVaultAutomatic(); setBannerLocal('Mode pratique activé : plus de demande de code à chaque ouverture.'); } catch (e) { setError(e.message || 'Impossible d’activer le mode pratique.'); }
              }}>✨ Ne plus demander de déverrouillage à l’ouverture</button> : <>
                <span className="small muted">Pour revenir au mode renforcé, choisissez un nouveau code. Il sera demandé au prochain rechargement complet (ou utilisable avec WebAuthn si vous l’activez ensuite).</span>
                <input type="password" autoComplete="new-password" value={vaultStrongCode} onChange={(e) => setVaultStrongCode(e.target.value)} placeholder="Nouveau code renforcé (8 car. min.)" />
                <input type="password" autoComplete="new-password" value={vaultStrongCode2} onChange={(e) => setVaultStrongCode2(e.target.value)} placeholder="Confirmer le code" />
                <button type="button" onClick={async () => {
                  if (vaultStrongCode.length < 8) return setError('Code renforcé : 8 caractères minimum.');
                  if (vaultStrongCode !== vaultStrongCode2) return setError('Les deux codes renforcés sont différents.');
                  try { await onDisableVaultAutomatic(vaultStrongCode); setVaultStrongCode(''); setVaultStrongCode2(''); setBannerLocal('Mode renforcé activé. Le code sera demandé au prochain rechargement complet.'); } catch (e) { setError(e.message || 'Impossible de revenir au mode renforcé.'); }
                }}>🔐 Revenir au mode renforcé</button>
              </>}
            </div>}
            {vaultEnabled && !vaultAutomatic && <details>
              <summary className="small">Changer le code du coffre</summary>
              <div className="vault-code-grid">
                <input type="password" autoComplete="current-password" value={vaultOldCode} onChange={(e) => setVaultOldCode(e.target.value)} placeholder="Code actuel" />
                <input type="password" autoComplete="new-password" value={vaultNewCode} onChange={(e) => setVaultNewCode(e.target.value)} placeholder="Nouveau code (8 car. min.)" />
                <input type="password" autoComplete="new-password" value={vaultNewCode2} onChange={(e) => setVaultNewCode2(e.target.value)} placeholder="Confirmer le nouveau code" />
                <button type="button" onClick={async () => {
                  setError('');
                  if (vaultNewCode.length < 8) return setError('Nouveau code du coffre : 8 caractères minimum.');
                  if (vaultNewCode !== vaultNewCode2) return setError('Les deux nouveaux codes du coffre sont différents.');
                  try { await onChangeVaultCode(vaultOldCode, vaultNewCode); setVaultOldCode(''); setVaultNewCode(''); setVaultNewCode2(''); setBannerLocal('Code du coffre local modifié.'); }
                  catch (e) { setError(e.message || 'Impossible de modifier le code du coffre.'); }
                }}>Modifier le code</button>
              </div>
            </details>}
            {vaultEnabled && !vaultAutomatic && <div className="vault-auth-block">
              <strong className="small">🔐 Déverrouillage biométrique / système</strong>
              <span className="small muted">Utilise WebAuthn PRF avec l’authentificateur de l’appareil : Face ID/Touch ID sur Apple, empreinte ou déverrouillage système sur Android, Windows Hello, etc. Aucun secret biométrique n’est envoyé au serveur.</span>
              {!vaultBiometricEnabled ? <button type="button" className="ghost" disabled={!vaultBiometricAvailable} onClick={async () => {
                setError(''); try { await onEnableVaultBiometric(); setBannerLocal('Déverrouillage biométrique du coffre activé.'); }
                catch (e) { setError(e.message || 'Impossible d’activer la biométrie du coffre.'); }
              }}>{vaultBiometricAvailable ? '🔐 Activer le déverrouillage biométrique / système' : 'WebAuthn PRF indisponible ici — code coffre disponible'}</button>
              : <button type="button" className="ghost" onClick={async () => { if (window.confirm('Désactiver le déverrouillage biométrique de ce coffre ? Le code restera disponible.')) { try { await onDisableVaultBiometric(); setBannerLocal('Déverrouillage biométrique désactivé.'); } catch (e) { setError(e.message || 'Impossible de désactiver la biométrie.'); } } }}>Désactiver le déverrouillage biométrique / système</button>}
            </div>}
            {vaultEnabled && !vaultAutomatic && <div className="vault-policy-block">
              <strong className="small">Quand reverrouiller le coffre ?</strong>
              <label><input type="radio" name="vault-lock-policy" style={{ width: 'auto' }} checked={vaultLockPolicy === 'launch'} onChange={() => onSetVaultLockPolicy('launch')} /><span className="small">Seulement à la prochaine ouverture complète / rechargement de l’app</span></label>
              <label><input type="radio" name="vault-lock-policy" style={{ width: 'auto' }} checked={vaultLockPolicy === 'background30'} onChange={() => onSetVaultLockPolicy('background30')} /><span className="small">Après 30 s en arrière-plan</span></label>
              <button type="button" className="ghost" onClick={onLockVaultNow}>🔒 Verrouiller le coffre maintenant</button>
            </div>}
            {vaultEnabled && <div className="vault-backup-block">
              <strong className="small">💾 Sauvegarde complète chiffrée</strong>
              <span className="small muted">Identité + clé privée + contacts + groupes + historique + médias + réglages. La DEK et la credential WebAuthn restent propres à cet appareil.</span>
              <input type="password" autoComplete="new-password" value={vaultBackupPass} onChange={(e) => setVaultBackupPass(e.target.value)} placeholder="Mot de passe de sauvegarde (8 car. min.)" />
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <button type="button" onClick={async () => { if (vaultBackupPass.length < 8) return setError('Mot de passe de sauvegarde : 8 caractères minimum.'); try { await onExportVaultBackup(vaultBackupPass); setBannerLocal('Sauvegarde complète chiffrée exportée.'); } catch (e) { setError(e.message || 'Export complet impossible.'); } }}>Exporter tout le coffre</button>
                <label className="ghost vault-file-label">Choisir sauvegarde…<input hidden type="file" accept=".json,application/json" onChange={(e) => setVaultBackupFile(e.target.files?.[0] || null)} /></label>
              </div>
              {vaultBackupFile && <button type="button" className="danger-soft" onClick={async () => {
                if (vaultBackupPass.length < 8) return setError('Mot de passe de sauvegarde : 8 caractères minimum.');
                if (!window.confirm('Restaurer cette sauvegarde complète remplacera l’identité et l’historique locaux de cet appareil. Continuer ?')) return;
                try { await onImportVaultBackup(vaultBackupFile, vaultBackupPass); } catch (e) { setError(e.message || 'Restauration complète impossible.'); }
              }}>Restaurer « {vaultBackupFile.name} »</button>}
            </div>}
          </div>

          <div className="setting-block app-lock-settings">
            <strong>🔐 Verrou UI historique</strong>
            {vaultEnabled ? <span className="small muted">Le stockage chiffré 4.9.4 remplace ce verrou pendant une session ouverte : utilisez la politique du coffre ci-dessus. L’ancien réglage reste conservé uniquement pour compatibilité.</span> : <>
            <span className="small muted">Masque seulement l’interface ; à utiliser uniquement si le coffre n’est pas disponible.</span>
            {!appLockConfigured ? <>
              <input type="password" autoComplete="new-password" value={lockCode} onChange={(e) => setLockCode(e.target.value)} placeholder="Nouveau code local (4 caractères min.)" />
              <input type="password" autoComplete="new-password" value={lockCode2} onChange={(e) => setLockCode2(e.target.value)} placeholder="Confirmer le code" />
              <button type="button" onClick={async () => {
                if (lockCode.length < 4) return setError('Code local : 4 caractères minimum.');
                if (lockCode !== lockCode2) return setError('Les deux codes locaux sont différents.');
                try { await onConfigureAppLock(lockCode); setLockCode(''); setLockCode2(''); setBannerLocal('Verrou local activé.'); }
                catch (e) { setError(e.message || 'Impossible d’activer le verrou local.'); }
              }}>Activer le verrou local</button>
            </> : <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={onLockAppNow}>🔒 Verrouiller maintenant</button>
              <button type="button" className="ghost" onClick={() => { if (window.confirm('Désactiver le verrou local de cet appareil ?')) onDisableAppLock(); }}>Désactiver</button>
            </div>}
            </>}
          </div>

          {isAdmin && <div className="admin-card">
            <div className="admin-title"><strong>🛡️ Administration serveur</strong><span className="admin-badge">clé épinglée</span></div>
            <p className="small muted">Actions disponibles uniquement pour cette identité authentifiée. Aucune commande shell n’est exposée au navigateur.</p>
            <div className="admin-actions">
              <button type="button" className="ghost" onClick={() => onAdminPreview('purge-expired')}>🧹 Purger les expirés maintenant</button>
              <button type="button" className="ghost" onClick={() => onAdminPreview('purge-undecipherable')}>🧩 Purger les indéchiffrables signalés</button>
              <button type="button" className="ghost" onClick={() => onAdminPreview('deep-clean')}>🧽 Nettoyage profond</button>
              <button type="button" className="danger-soft" onClick={() => onAdminPreview('purge-queue')}>🔥 Vider toute la file serveur</button>
              <button type="button" className="danger" onClick={() => onAdminPreview('panic-server')}>☢️ PANIC serveur</button>
            </div>
            {adminError && <p className="error">{adminError}</p>}
            {adminResult && <div className="admin-result small">
              ✅ Action « {adminResult.action} » terminée · {Number(adminResult.removedMessages || 0)} messages · {Number(adminResult.removedMedia || 0)} médias · {formatBytes(adminResult.removedMediaBytes || 0)} · {Number(adminResult.removedCallSignals || 0)} signaux d’appel
            </div>}
            {adminPreview && <div className="admin-confirm">
              <strong>{adminPreview.title}</strong>
              <p className="small">{adminPreview.note}</p>
              <div className="server-stats">
                <span className="small">File actuelle : {Number(adminPreview.counts?.pendingMessages || 0)} messages · {Number(adminPreview.counts?.pendingMedia || 0)} médias ({formatBytes(adminPreview.counts?.pendingMediaBytes || 0)})</span>
                <span className="small">Comptes : {Number(adminPreview.counts?.users || 0)} · Push : {Number(adminPreview.counts?.pushSubscriptions || 0)} · Signaux appel : {Number(adminPreview.counts?.callSignals || 0)}</span>
                {adminPreview.action === 'purge-expired' && <span className="small">Arrivés à échéance : {Number(adminPreview.counts?.expiringMessages || 0)} messages · {Number(adminPreview.counts?.expiringMedia || 0)} médias</span>}
                {adminPreview.action === 'purge-undecipherable' && <span className="small">Signalés ≥3 fois : {Number(adminPreview.counts?.undecryptableMessages || 0)} messages · {Number(adminPreview.counts?.undecryptableMedia || 0)} médias ({formatBytes(adminPreview.counts?.undecryptableMediaBytes || 0)})</span>}
              </div>
              <label className="small muted">Recopiez exactement : <strong>{adminPreview.phrase}</strong></label>
              <input value={adminPhrase} onChange={(e) => setAdminPhrase(e.target.value)} placeholder={adminPreview.phrase} autoComplete="off" />
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <button type="button" className={adminPreview.action === 'panic-server' ? 'danger' : ''} disabled={adminPhrase !== adminPreview.phrase} onClick={() => onAdminExecute(adminPreview.token, adminPhrase)}>Confirmer l’action</button>
                <button type="button" className="ghost" onClick={onAdminCancel}>Annuler</button>
              </div>
              <span className="small muted">Confirmation valable 60 secondes et à usage unique. PANIC n’efface pas les appareils distants.</span>
            </div>}
          </div>}

          <div className="small muted">Dernier export d’historique : {lastExportAt ? new Date(lastExportAt).toLocaleString('fr-FR') : 'jamais'}</div>
          {safeBlocked.length > 0 && (
            <div>
              <span className="small muted">Contacts bloqués :</span>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {safeBlocked.map((p) => (
                  <span key={p} className="reaction">
                    {p}
                    <button type="button" className="ghost" style={{ padding: '0 4px' }}
                      onClick={() => onToggleBlocked(p)} title="Débloquer">✕</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <button type="button" onClick={onSecure} className={secured ? 'ghost' : ''}>
            {secured ? '🔐 Changer de passphrase' : '🔐 Sécuriser mon compte (passphrase)'}
          </button>
          <button type="button" className="ghost" onClick={() => setShowMyQr(true)}>
            <Icon.qrcode /> Afficher mon QR code
          </button>

          <form className="row" onSubmit={doExportIdentity}>
            <input type="password" value={exportPass} placeholder="Passphrase (export identité)"
                   onChange={(e) => setExportPass(e.target.value)} />
            <button>Exporter l'identité</button>
          </form>

          <form className="group-form" onSubmit={doExportHistory}>
            <input type="password" value={histPass} placeholder="Mot de passe (export/import historique)"
                   onChange={(e) => setHistPass(e.target.value)} />
            <div className="row">
              <button className="grow">Exporter l'historique</button>
              <label className="ghost" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', border: '1px solid var(--line)', borderRadius: 12, padding: '0 12px' }}>
                Choisir un fichier…
                <input type="file" hidden accept=".json,application/json" onChange={(e) => setImportFile(e.target.files[0])} />
              </label>
            </div>
            {importFile && <button type="button" className="ghost" onClick={doImportHistory}>Importer « {importFile.name} » (remplace l'historique local)</button>}
          </form>
          {bannerLocal && <p className="small muted">{bannerLocal}</p>}

          <button type="button" className="danger" onClick={() => setConfirmDelAccount(true)}>Supprimer mon compte</button>
          <button type="button" className="ghost" onClick={onLogout}>Se déconnecter (efface cet appareil)</button>
          <p className="muted small">Export d'historique : fichier chiffré par mot de passe, jamais envoyé au serveur. Vos conversations restent toujours locales.</p>
          <p className="muted small">Version {APP_VERSION}</p>
        </div>
      )}

      <div className="pad">
        <div className="row">
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Rechercher ou saisir un pseudo…"
                 onKeyDown={(e) => { if (e.key === 'Enter') startConversation(e); }} />
          <button onClick={startConversation}>Ouvrir</button>
          <button type="button" className="ghost" title="Scanner un QR code" onClick={() => setShowScanner(true)}><Icon.qrcode /></button>
          <button type="button" className="ghost" title="Créer un groupe" onClick={() => setShowNewGroup(!showNewGroup)}><Icon.group /></button>
        </div>
        {showNewGroup && (
          <form onSubmit={createGroup} className="group-form">
            <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Nom du groupe" required />
            <input value={groupMembers} onChange={(e) => setGroupMembers(e.target.value)} placeholder="Membres (virgules, 8 max)" required />
            <p className="muted small">Le groupe n'existe que sur les appareils des membres : le serveur n'en sait rien.</p>
            <button>Créer le groupe</button>
          </form>
        )}
        {error && <p className="error">{error}</p>}
      </div>

      {q.length >= 2 && (searchingMessages || messageResults.length > 0) && <div className="local-search-results pad">
        <div className="row" style={{ justifyContent: 'space-between' }}><strong>Dans les messages</strong><span className="small muted">100 % local</span></div>
        {searchingMessages && <div className="small muted">Recherche…</div>}
        {!searchingMessages && messageResults.map((result) => {
          const group = groups.find((g) => g.groupId === result.peer);
          const label = result.peer === identityPseudo ? 'Notes personnelles' : (group?.name || result.peer);
          return <button type="button" className="message-search-hit" key={`${result.peer}-${result.localId}`} onClick={() => onOpen(result.peer, result.localId)}>
            <span><strong>{label}</strong>{result.from && result.from !== identityPseudo ? ` · ${result.from}` : ''}</span>
            <span className="small">{result.excerpt}</span>
            <span className="small muted">{new Date(result.sentAt).toLocaleString('fr-FR')}</span>
          </button>;
        })}
      </div>}

      <ul className="conv-list">
        {filtered.length === 0 && (
          <li className="muted pad" style={{ background: 'transparent' }}>
            {q ? 'Aucun résultat.' : 'Aucune conversation. Saisissez un pseudo ci-dessus.'}
          </li>
        )}
        {filtered.map((it) => {
          const meta = safeConvMeta[it.key];
          const isBlocked = !it.group && !it.pinned && safeBlocked.includes(it.key);
          return (
            <li key={it.key} onClick={() => onOpen(it.key)}>
              <Avatar name={it.label} group={it.group} />
              <div className="grow">
                <strong>{it.label}</strong>
                {isBlocked && <span className="small muted"> (bloqué)</span>}
                {meta?.unread > 0 && <span className="badge">{meta.unread}</span>}
                <div className="muted small preview">
                  {it.group && !meta?.last ? (it.members || []).join(', ') : previewOf(meta)}
                </div>
              </div>
              {!it.group && !it.pinned && <button className="ghost conv-call-button" title="Appel audio" disabled={!audioCallAvailable || isBlocked}
                onClick={async (e) => { e.stopPropagation(); try { setError(''); await onStartAudioCallFor(it.key); } catch (err) { setError(err.message || 'Appel impossible.'); } }}><Icon.phone /></button>}
              {!it.group && !it.pinned && <button className="ghost conv-call-button" title="Appel vidéo" disabled={!videoCallAvailable || isBlocked}
                onClick={async (e) => { e.stopPropagation(); try { setError(''); await onStartVideoCallFor(it.key); } catch (err) { setError(err.message || 'Appel vidéo impossible.'); } }}><Icon.video /></button>}
              {!it.pinned && <div className="conv-more-wrap">
                <button className="ghost conv-more-button" title="Plus d’options" aria-expanded={openConversationMenu === it.key}
                  onClick={(e) => toggleConversationMenu(e, it.key)}>⋯</button>
                {openConversationMenu === it.key && typeof document !== 'undefined' && createPortal(
                  <div className="conv-menu-backdrop" onClick={closeConversationMenu} role="presentation">
                    <div className="conv-more-menu conv-more-menu-centered" role="dialog" aria-modal="true" aria-label={`Options pour ${it.label}`} onClick={(e) => e.stopPropagation()}>
                      <div className="conv-menu-title"><Avatar name={it.label} group={it.group} /><strong>{it.label}</strong></div>
                      <button type="button" onClick={() => { onSetConversationPinned(it.key, !pinnedSet.has(it.key)); closeConversationMenu(); }}>{pinnedSet.has(it.key) ? '📌 Désépingler' : '📍 Épingler'}</button>
                      <button type="button" onClick={() => { onSetConversationArchived(it.key, !archivedSet.has(it.key)); closeConversationMenu(); }}>{archivedSet.has(it.key) ? '↩ Sortir des archives' : '📥 Archiver'}</button>
                      {!it.group && <button type="button" onClick={() => { onToggleBlocked(it.key); closeConversationMenu(); }}>{isBlocked ? '✅ Débloquer' : '🚫 Bloquer'}</button>}
                      <button type="button" className="danger-text" onClick={() => { setConfirmDelConv({ key: it.key, label: it.label }); closeConversationMenu(); }}>🗑️ Supprimer</button>
                      <button type="button" className="ghost conv-menu-close" onClick={closeConversationMenu}>Fermer</button>
                    </div>
                  </div>, document.body)}
              </div>}
            </li>
          );
        })}
      </ul>

      {!q && archivedItems.length > 0 && <div className="archived-block pad">
        <button type="button" className="ghost archived-toggle" onClick={() => setShowArchived((v) => !v)}>📦 Archives ({archivedItems.length}) {showArchived ? '▴' : '▾'}</button>
        {showArchived && <ul className="conv-list archived-list">{archivedItems.map((it) => {
          const meta = safeConvMeta[it.key];
          return <li key={`arch-${it.key}`} onClick={() => onOpen(it.key)}>
            <Avatar name={it.label} group={it.group} /><div className="grow"><strong>{it.label}</strong>{meta?.unread > 0 && <span className="badge">{meta.unread}</span>}<div className="muted small preview">{previewOf(meta)}</div></div>
            {!it.group && <button type="button" className="ghost conv-call-button" title="Appel audio" disabled={!audioCallAvailable} onClick={async (e) => { e.stopPropagation(); try { await onStartAudioCallFor(it.key); } catch (err) { setError(err.message || 'Appel impossible.'); } }}><Icon.phone /></button>}
            {!it.group && <button type="button" className="ghost conv-call-button" title="Appel vidéo" disabled={!videoCallAvailable} onClick={async (e) => { e.stopPropagation(); try { await onStartVideoCallFor(it.key); } catch (err) { setError(err.message || 'Appel vidéo impossible.'); } }}><Icon.video /></button>}
            <button type="button" className="ghost" title="Sortir des archives" onClick={(e) => { e.stopPropagation(); onSetConversationArchived(it.key, false); }}>⋯</button>
          </li>;
        })}</ul>}
      </div>}

      {/* Bottom sheet : confirmation suppression de conversation */}
      {confirmDelConv && (
        <div className="sheet-backdrop" onClick={() => setConfirmDelConv(null)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h1>Supprimer « {confirmDelConv.label} » ?</h1>
            <p className="muted small">Suppression locale irréversible (n'affecte pas les autres appareils ni vos contacts).</p>
            <button className="danger" onClick={() => { onDeleteConversation(confirmDelConv.key); setConfirmDelConv(null); }}>Supprimer</button>
            <button className="ghost" onClick={() => setConfirmDelConv(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Bottom sheet : suppression de compte (double confirmation) */}
      {confirmDelAccount && (
        <div className="sheet-backdrop" onClick={() => setConfirmDelAccount(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h1>Supprimer définitivement le compte</h1>
            <p className="muted small">
              Le serveur supprimera votre compte et tous vos messages en attente ;
              cet appareil sera purgé. Action IRRÉVERSIBLE. Tapez votre pseudo
              (<strong>{identityPseudo}</strong>) pour confirmer.
            </p>
            <input value={delAccountText} onChange={(e) => setDelAccountText(e.target.value)} placeholder="Votre pseudo" />
            <button className="danger" disabled={delAccountText !== identityPseudo}
              onClick={() => { setConfirmDelAccount(false); onDeleteAccount(); }}>Supprimer mon compte</button>
            <button className="ghost" onClick={() => setConfirmDelAccount(false)}>Annuler</button>
          </div>
        </div>
      )}
      {/* Bottom sheet : mon QR code (pseudo + clé publique, rien de secret) */}
      {showMyQr && (
        <div className="sheet-backdrop" onClick={() => setShowMyQr(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <h1>Mon QR code</h1>
            <p className="muted small">Faites-le scanner par votre contact pour qu'il vous ajoute avec une clé vérifiée — plus fiable que comparer le fingerprint à la main.</p>
            {identityPublicKey
              ? <QrCode text={qrPayload(identityPseudo, identityPublicKey)} />
              : <p className="error">Clé publique locale illisible.</p>}
            <p className="small muted">{identityPseudo}</p>
            <button className="ghost" onClick={() => setShowMyQr(false)}>Fermer</button>
          </div>
        </div>
      )}

      {/* Scanner QR : ajoute/vérifie un contact à partir du QR affiché par l'autre personne. */}
      {showScanner && (
        <QrScanner onResult={handleScanResult} onClose={() => setShowScanner(false)} />
      )}
    </div>
  );
}
